import React from "react";
import type { Product } from "./types";
import { DEFAULT_PERMISSIONS } from "./constants";

/**
 * Calculate visual readiness score for a product
 * Checks hero image, detail photos (4+ needed), and selling features (5+ needed)
 */
export function getVisualsReadiness(product: Product | null | undefined): {
  percent: number;
  photoCount: number;
} {
  if (!product) return { percent: 0, photoCount: 0 };

  const photoCount = (product.detailPhotos || []).filter(Boolean).length;

  // Visuals ready = hero image present
  return { percent: product.heroImage ? 100 : 0, photoCount };
}

/**
 * Calculate copy readiness score for a product
 * Checks selling features, dimensions, material, target audience, use cases, category, and backend keywords
 */
export function getCopyReadiness(product: Product | null | undefined): {
  percent: number;
} {
  if (!product) return { percent: 0 };

  let score = 0;
  const total = 7;

  if ((product.sellingFeatures || []).length >= 5) score++;
  else if ((product.sellingFeatures || []).length >= 3) score += 0.5;

  if (product.dimensions) score++;
  if (product.material) score++;
  if (product.targetAudience) score++;
  if (product.useCases) score++;
  if (product.category) score++;
  if (product.backendKeywords) score++;

  return { percent: Math.round((score / total) * 100) };
}

/**
 * Escape a value for safe CSV export
 * Handles quotes, commas, and formula injection prevention
 */
export function csvSafe(val: unknown): string {
  let s = String(val ?? "").replace(/"/g, '""');
  if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
  return `"${s}"`;
}

// ─── Permission Helpers ──────────────────────────────────────

export function getPermission(permissions: Record<string, Record<string, string>> | null, area: string, role: string): string {
  const saved = permissions && permissions[area] && permissions[area][role];
  if (saved) return saved;
  // Fall back to defaults for new permission keys not yet in saved settings
  const defaults = DEFAULT_PERMISSIONS as Record<string, Record<string, string>>;
  return (defaults[area] && defaults[area][role]) || "none";
}

export function canAccess(permissions: Record<string, Record<string, string>> | null, area: string, role: string): boolean {
  return getPermission(permissions, area, role) !== "none";
}

export function canEdit(permissions: Record<string, Record<string, string>> | null, area: string, role: string): boolean {
  const perm = getPermission(permissions, area, role);
  return perm === "full" || perm === "edit";
}

// ─── Error Boundary ──────────────────────────────────────────

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("PXM Dash caught error:", error, info);
    // Auto-reload on stale chunk errors (happens after new deploys)
    if (this.isChunkError(error)) {
      console.warn("[ErrorBoundary] Stale chunk detected — reloading page");
      window.location.reload();
    }
  }

  private isChunkError(error: Error): boolean {
    const msg = error?.message || "";
    return (
      msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("Loading chunk") ||
      msg.includes("Loading CSS chunk") ||
      msg.includes("Importing a module script failed")
    );
  }

  render() {
    if (this.state.hasError) {
      if (this.isChunkError(this.state.error!)) {
        return (
          <div className="error-boundary-wrap">
            <div className="error-boundary-updating">Updating to latest version...</div>
          </div>
        );
      }
      return (
        <div className="error-boundary-wrap">
          <div className="error-boundary-icon">⚠</div>
          <h2 className="error-boundary-heading">Something went wrong</h2>
          <p className="error-boundary-msg">{this.state.error?.message || "An unexpected error occurred"}</p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="error-boundary-btn"
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Returns true if a hex color is "light" (should use dark text).
 * Uses relative luminance calculation.
 */
export function isLightColor(hex: string): boolean {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 0.65;
}

/** Avatar color palette — simplified 4-color brand palette */
export const AVATAR_COLORS: { bg: string; text: string }[] = [
  { bg: '#E28C7A', text: '#FFFFFF' }, // Peach
  { bg: '#31363d', text: '#FFFFFF' }, // Charcoal
  { bg: '#D15865', text: '#FFFFFF' }, // Rose (functional)
  { bg: '#6B6863', text: '#FFFFFF' }, // Mid
  { bg: '#8A8784', text: '#FFFFFF' }, // Light
  { bg: '#EBE7E4', text: '#31363d' }, // Border
];

/**
 * Get a deterministic avatar color pair from a name string.
 * If a saved hex color is provided, use it instead of the hash.
 * Returns { bg, text } for background and text color.
 */
export function avatarColor(name: string | undefined | null, savedColor?: string): { bg: string; text: string } {
  if (savedColor) {
    const match = AVATAR_COLORS.find(c => c.bg.toLowerCase() === savedColor.toLowerCase());
    if (match) return match;
    return { bg: savedColor, text: isLightColor(savedColor) ? '#31363d' : '#FFFFFF' };
  }
  if (!name) return { bg: '#31363d', text: '#FFFFFF' };
  const hash = [...name].reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

/**
 * Look up avatar color for a name from a team members list.
 * Uses the member's saved color if available, otherwise falls back to hash.
 * Also returns avatarUrl if the member has one.
 */
export function memberAvatarColor(
  name: string | undefined | null,
  teamMembers: { name?: string; email?: string; color?: string; avatarUrl?: string; initials?: string }[] = [],
): { bg: string; text: string; avatarUrl?: string; initials: string } {
  if (name && teamMembers.length > 0) {
    const member = teamMembers.find(m => m.name === name || m.email === name);
    if (member) {
      const displayName = member.name || name;
      const initials = member.initials || displayName.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
      return {
        ...avatarColor(displayName, member.color),
        avatarUrl: member.avatarUrl,
        initials,
      };
    }
  }
  const fallbackName = name || "?";
  const initials = fallbackName.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || fallbackName[0]?.toUpperCase() || "?";
  return { ...avatarColor(name), initials };
}

// ─── Shared: displayTitle ───────────────────────────────────
const TITLE_DISPLAY_MAX = 60;
export function displayTitle(title: string | undefined): string {
  if (!title) return "—";
  let t = title
    .replace(/^U\s*Brands?\s*/i, "")
    .replace(/,\s*Whiteboard Pens.*/i, "")
    .replace(/,\s*Dry Erase Markers? For.*/i, "")
    .replace(/\s*with Chisel Point.*/i, "")
    .replace(/\s*with Chisel Tip.*/i, "")
    .replace(/\s*Chisel Tip Set,?\s*/i, "Chisel Tip ")
    .replace(/\s+/g, " ")
    .trim();
  if (t.length > TITLE_DISPLAY_MAX) {
    t = t.substring(0, TITLE_DISPLAY_MAX);
    const lastSpace = t.lastIndexOf(" ");
    if (lastSpace > TITLE_DISPLAY_MAX * 0.6) t = t.substring(0, lastSpace);
    t = t.replace(/[,\s]+$/, "");
  }
  return t;
}

// ─── Shared: readSSEStream ──────────────────────────────────
export async function readSSEStream(response: Response): Promise<string> {
  if (!response.body) throw new Error("No response body for SSE stream");
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let fullText = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const data = line.slice(6).trim();
      if (data === "[DONE]") continue;
      try {
        const evt = JSON.parse(data);
        if (evt.text) fullText += evt.text;
        if (evt.error) throw new Error(evt.error);
      } catch (e) {
        if (e instanceof SyntaxError) continue;
        throw e;
      }
    }
  }
  return fullText;
}

// ─── Shared: Backend Keywords max length ────────────────────
export const BACKEND_KEYWORDS_MAX = 249;

// ─── Shared: image upload to storage ────────────────────────
export const uploadToStorage = async (dataUrl: string, folder: string, filename: string): Promise<string> => {
  const { authFetch } = await import('./supabase');
  try {
    const res = await authFetch("/api/upload-image", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ dataUrl, folder, filename }),
    });
    const data = await res.json() as { success?: boolean; url?: string; error?: string };
    if (data.success && data.url) return data.url;
    console.error("[uploadToStorage] Failed:", data.error);
    return dataUrl; // fallback to base64 if upload fails
  } catch (err) {
    console.error("[uploadToStorage] Error:", err);
    return dataUrl; // fallback to base64
  }
};

// ─── Shared: client-side image compression ──────────────────
export const compressImage = (dataUrl: string, maxDim: number, quality = 0.7): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d")?.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
};
